/**
 * Process Analytics Dashboard
 * FERP UNICORN 3: Bottleneck detection, SLA tracking, optimization recommendations
 */

import React, { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Tabs,
  Badge,
  Button,
  Grid,
  RingProgress,
  Progress,
  Table,
  Alert,
  ThemeIcon,
  Tooltip
} from '@mantine/core';
import {
  IconChartBar,
  IconAlertTriangle,
  IconClock,
  IconSparkles,
  IconRefresh,
  IconTrendingUp,
  IconTrendingDown,
  IconCheck,
  IconX
} from '@tabler/icons-react';
import { useProcessMetrics, type Bottleneck, type OptimizationRecommendation } from '../hooks/useProcessMetrics';

export interface ProcessAnalyticsDashboardProps {
  workflowId: string;
  workflowName: string;
  timeRange: { start: Date; end: Date };
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

function getBottleneckColor(percentOfTotal: number): string {
  if (percentOfTotal > 30) return 'red';
  if (percentOfTotal > 10) return 'yellow';
  return 'green';
}

export function ProcessAnalyticsDashboard({
  workflowId,
  workflowName,
  timeRange
}: ProcessAnalyticsDashboardProps) {
  const { analytics, metrics, bottlenecks, slaCompliance, recommendations, loading, refetch } = useProcessMetrics(workflowId, timeRange);
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  if (!metrics && !loading) {
    return (
      <Alert color="blue" icon={<IconChartBar size={16} />}>
        <Text size="sm">No analytics data available for this workflow yet.</Text>
      </Alert>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Text size="lg" fw={600}>{workflowName}</Text>
            <Text size="xs" c="dimmed">Process Analytics & Mining</Text>
          </div>
          <Button
            size="xs"
            variant="light"
            onClick={() => refetch()}
            loading={loading}
            leftSection={<IconRefresh size={14} />}
          >
            Refresh
          </Button>
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="bottlenecks" leftSection={<IconAlertTriangle size={16} />}>
              Bottlenecks
            </Tabs.Tab>
            <Tabs.Tab value="sla" leftSection={<IconClock size={16} />}>
              SLA
            </Tabs.Tab>
            <Tabs.Tab value="optimizations" leftSection={<IconSparkles size={16} />}>
              Optimizations
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              {/* Key Metrics Cards */}
              <Grid>
                <Grid.Col span={4}>
                  <Card padding="md" withBorder>
                    <Stack gap="xs">
                      <Text size="xs" c="dimmed">Total Executions</Text>
                      <Text size="xl" fw={700}>{metrics?.totalExecutions || 0}</Text>
                      <Group gap={4}>
                        <ThemeIcon size="xs" color="green" variant="light">
                          <IconCheck size={12} />
                        </ThemeIcon>
                        <Text size="xs" c="dimmed">
                          {Math.round((metrics?.successRate || 0) * 100)}% success
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={4}>
                  <Card padding="md" withBorder>
                    <Stack gap="xs">
                      <Text size="xs" c="dimmed">Avg Execution Time</Text>
                      <Text size="xl" fw={700}>{formatDuration(metrics?.avgExecutionTime || 0)}</Text>
                      <Text size="xs" c="dimmed">
                        P95: {formatDuration(metrics?.p95ExecutionTime || 0)}
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={4}>
                  <Card padding="md" withBorder>
                    <Stack gap="xs">
                      <Text size="xs" c="dimmed">SLA Compliance</Text>
                      <Text size="xl" fw={700}>{Math.round((slaCompliance?.complianceRate || 0) * 100)}%</Text>
                      <Group gap={4}>
                        <ThemeIcon
                          size="xs"
                          color={(slaCompliance?.complianceRate || 0) >= 0.95 ? 'green' : 'red'}
                          variant="light"
                        >
                          {(slaCompliance?.complianceRate || 0) >= 0.95 ? <IconCheck size={12} /> : <IconX size={12} />}
                        </ThemeIcon>
                        <Text size="xs" c="dimmed">
                          {slaCompliance?.violationCount || 0} violations
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Performance Distribution */}
              <Card padding="md" withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={600}>Performance Distribution</Text>
                  <Group gap="xl">
                    <div style={{ flex: 1 }}>
                      <Text size="xs" c="dimmed" mb={4}>P50 (Median)</Text>
                      <Progress value={50} size="lg" color="green" />
                      <Text size="xs" mt={4}>{formatDuration(metrics?.p50ExecutionTime || 0)}</Text>
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text size="xs" c="dimmed" mb={4}>P95</Text>
                      <Progress value={95} size="lg" color="yellow" />
                      <Text size="xs" mt={4}>{formatDuration(metrics?.p95ExecutionTime || 0)}</Text>
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text size="xs" c="dimmed" mb={4}>P99</Text>
                      <Progress value={99} size="lg" color="orange" />
                      <Text size="xs" mt={4}>{formatDuration(metrics?.p99ExecutionTime || 0)}</Text>
                    </div>
                  </Group>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Bottlenecks Tab */}
          <Tabs.Panel value="bottlenecks" pt="md">
            <Stack gap="md">
              <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  Bottlenecks are workflow nodes consuming the most execution time.
                </Text>
              </Alert>

              {bottlenecks.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Node</Table.Th>
                      <Table.Th>Avg Duration</Table.Th>
                      <Table.Th>% of Total</Table.Th>
                      <Table.Th>Recommendation</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bottlenecks.map((bn) => (
                      <Table.Tr key={bn.nodeId}>
                        <Table.Td>
                          <Text size="sm" fw={500}>{bn.nodeName}</Text>
                          <Text size="xs" c="dimmed">{bn.executionCount} executions</Text>
                        </Table.Td>
                        <Table.Td>{formatDuration(bn.avgDuration)}</Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Progress
                              value={bn.percentOfTotal}
                              color={getBottleneckColor(bn.percentOfTotal)}
                              style={{ flex: 1, maxWidth: 100 }}
                            />
                            <Badge size="sm" color={getBottleneckColor(bn.percentOfTotal)}>
                              {Math.round(bn.percentOfTotal)}%
                            </Badge>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{bn.recommendation}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  <Text size="sm">No significant bottlenecks detected.</Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          {/* SLA Tab */}
          <Tabs.Panel value="sla" pt="md">
            <Stack gap="md">
              <Group justify="center">
                <RingProgress
                  size={180}
                  thickness={16}
                  sections={[{ value: (slaCompliance?.complianceRate || 0) * 100, color: (slaCompliance?.complianceRate || 0) >= 0.95 ? 'green' : 'red' }]}
                  label={
                    <div style={{ textAlign: 'center' }}>
                      <Text size="xl" fw={700}>{Math.round((slaCompliance?.complianceRate || 0) * 100)}%</Text>
                      <Text size="xs" c="dimmed">Compliant</Text>
                    </div>
                  }
                />
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <Card padding="md" withBorder>
                    <Text size="xs" c="dimmed" mb={4}>Target SLA</Text>
                    <Text size="lg" fw={600}>{formatDuration(slaCompliance?.targetSLA || 0)}</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Card padding="md" withBorder>
                    <Text size="xs" c="dimmed" mb={4}>Actual Avg</Text>
                    <Text size="lg" fw={600}>{formatDuration(slaCompliance?.actualAvg || 0)}</Text>
                  </Card>
                </Grid.Col>
              </Grid>

              {(slaCompliance?.violationCount || 0) > 0 && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm" fw={600}>{slaCompliance?.violationCount} SLA Violations</Text>
                  <Text size="xs">Recent violations require attention.</Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Optimizations Tab */}
          <Tabs.Panel value="optimizations" pt="md">
            <Stack gap="md">
              {recommendations.length > 0 ? (
                recommendations.map((rec, i) => (
                  <Card key={i} padding="md" withBorder>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <ThemeIcon size="lg" color="violet" variant="light">
                            <IconSparkles size={20} />
                          </ThemeIcon>
                          <div>
                            <Text size="sm" fw={600}>{rec.type} Optimization</Text>
                            <Text size="xs" c="dimmed">Estimated {Math.round(rec.estimatedImprovement)}% improvement</Text>
                          </div>
                        </Group>
                        <Badge color="violet">{Math.round(rec.confidence * 100)}% confidence</Badge>
                      </Group>
                      <Text size="sm">{rec.description}</Text>
                      <Group gap="xs">
                        <IconClock size={14} />
                        <Text size="xs" c="dimmed">Effort: ~{rec.effort}h</Text>
                      </Group>
                    </Stack>
                  </Card>
                ))
              ) : (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  <Text size="sm">Workflow is well-optimized. No recommendations at this time.</Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
}

export default ProcessAnalyticsDashboard;
